import { useMemo } from 'react';

import { DEFAULT_PAGE_SIZE } from 'consts/placeholders';
import {
  usePartnersQuery,
  useQueryCounties,
  useQueryRenters,
  useQueryReservationLanguages,
  useQueryReservationStatus,
  useQueryReservationTypeOfService,
} from 'modules/_common/';
import {
  ECommonDropdownValues,
  TCommonDropdownValues,
} from 'modules/_common/consts/commonDropdownValues';
import { transformCommonDropdownValues } from 'modules/_common/helper';

export const useCommonDropdownValues = (
  type: TCommonDropdownValues[],
  typeOfServiceFilter?: string | null | undefined,
) => {
  const isEnabled = (dropdownValue: TCommonDropdownValues) => type.includes(dropdownValue);

  const { partnersSuppliers, errPartnersSuppliers, isLoadingPartnersSuppliers } = usePartnersQuery(
    {
      pageNo: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    isEnabled(ECommonDropdownValues.Partners),
  );

  const {
    typeOfServiceData,
    error: errTypeOfServiceData,
    loading: isLoadingTypeOfServiceData,
  } = useQueryReservationTypeOfService(
    typeOfServiceFilter,
    isEnabled(ECommonDropdownValues.TypeOfService),
  );

  const {
    reservationLanguages,
    error: errLanguagesError,
    loading: isLoadingLanguagesError,
  } = useQueryReservationLanguages(isEnabled(ECommonDropdownValues.Languages));

  const { reservationStatuses, errReservationStatuses, isLoadingReservationStatuses } =
    useQueryReservationStatus(isEnabled(ECommonDropdownValues.Statuses));

  const { rentersData, rentersIsLoading, rentersIsError } = useQueryRenters(
    {
      pageNo: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    isEnabled(ECommonDropdownValues.Renters),
  );

  const {
    data: counties,
    isLoading: isLoadingCounties,
    isError: isErrorCounties,
  } = useQueryCounties(isEnabled(ECommonDropdownValues.Counties));

  const {
    transformPartnersSuppliers,
    transformTypeOfServiceData,
    transformReservationLanguages,
    transformReservationStatuses,
    transformRenters,
    transformCounties,
  } = transformCommonDropdownValues();

  const dropdownValues = useMemo(() => {
    const partners = transformPartnersSuppliers(partnersSuppliers?.items);
    const serviceDataType = transformTypeOfServiceData(typeOfServiceData);
    const reservationLanguage = transformReservationLanguages(reservationLanguages);
    const formedReservationStatus = transformReservationStatuses(reservationStatuses!);
    const renters = transformRenters(rentersData);
    const countiesList = transformCounties(counties?.items);

    return {
      partners,
      serviceDataType,
      reservationLanguage,
      formedReservationStatus,
      renters,
      countiesList,
    };
  }, [
    partnersSuppliers,
    typeOfServiceData,
    reservationLanguages,
    reservationStatuses,
    rentersData,
    counties,
  ]);

  return {
    dropdownValues,
    isLoadingCommonDropdownValues:
      isLoadingPartnersSuppliers ||
      isLoadingTypeOfServiceData ||
      isLoadingLanguagesError ||
      isLoadingReservationStatuses ||
      rentersIsLoading ||
      isLoadingCounties,
    isErrorCommonDropdownValues:
      errPartnersSuppliers ||
      errTypeOfServiceData ||
      errReservationStatuses ||
      errLanguagesError ||
      rentersIsError ||
      isErrorCounties,
  };
};
